"use client";

import type { ReactNode } from "react";
import { Star, ShieldCheck, Users } from "lucide-react";

type ServiceItem = {
  title: string;
  vendor: string;
  description: string;
  price: string;
  priceNote?: string;
  oldPrice?: string;
  buttonLabel: string;
  rating: string;
  icon: ReactNode;
  heroClass: string;
  vendorOfficial?: boolean;
  accentPrice?: boolean;
};

type ServicesCarouselProps = {
  items: ServiceItem[];
};

export default function ServicesCarousel({ items }: ServicesCarouselProps) {
  if (!items?.length) return null;

  return (
    <div className="relative overflow-hidden w-full">
      
      {/* 1. INJEÇÃO DO CSS DA ANIMAÇÃO */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes infinite-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            /* 40s é a velocidade. Aumente ou diminua para ajustar */
            animation: infinite-scroll 40s linear infinite; 
          }
          .animate-marquee:hover {
            animation-play-state: paused; /* Pausa quando o mouse passa por cima */
          }
        `
      }} />

      {/* Sombras Laterais */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-[#09090b] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-[#09090b] to-transparent" />

      {/* 2. CONTAINER ANIMADO (Ele contém os itens em dobro para o loop perfeito) */}
      <div className="animate-marquee flex w-max py-4">
        
        {/* GRUPO 1 (Itens Originais) */}
        <div className="flex gap-6 px-3">
          {items.map((service, index) => (
            <article
              key={`original-${service.title}-${index}`}
              className="flex min-h-[470px] w-[320px] shrink-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111827] transition-all hover:border-[#5b5cff]/50 hover:shadow-[0_0_30px_rgba(91,92,255,0.15)]"
            >
              <div className={`relative flex h-40 w-full flex-col justify-end bg-gradient-to-br ${service.heroClass} p-6`}>
                <div className="absolute right-4 top-4 flex items-center gap-1 rounded-md border border-white/10 bg-black/50 px-2 py-1 text-xs text-white backdrop-blur-md">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  {service.rating}
                </div>
                {service.icon}
                <h3 className="text-xl font-bold text-white">{service.title}</h3>
              </div>

              <div className="flex flex-1 flex-col p-5">
                <div className="mb-2 flex items-center gap-2 text-xs text-zinc-400">
                  {service.vendorOfficial ? (
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <Users className="h-4 w-4 text-zinc-500" />
                  )}
                  <span>Por {service.vendor}</span>
                </div>
                <p className="mb-4 text-sm leading-6 text-zinc-300">
                  {service.description}
                </p>
                <div className="mt-auto flex items-end justify-between gap-4">
                  <div>
                    {service.oldPrice ? (
                      <span className="text-xs text-zinc-500 line-through">
                        {service.oldPrice}
                      </span>
                    ) : null}
                    <div className={["text-lg font-bold", service.accentPrice ? "text-[#22d3ee]" : "text-white"].join(" ")}>
                      {service.price}
                      {service.priceNote ? (
                        <span className="text-sm font-normal text-zinc-400">
                          {service.priceNote}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5b5cff]">
                    {service.buttonLabel}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* GRUPO 2 (Clone Fantasma para criar a ilusão de infinito) */}
        <div className="flex gap-6 px-3" aria-hidden="true">
          {items.map((service, index) => (
            <article
              key={`clone-${service.title}-${index}`}
              className="flex min-h-[470px] w-[320px] shrink-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111827] transition-all hover:border-[#5b5cff]/50 hover:shadow-[0_0_30px_rgba(91,92,255,0.15)]"
            >
              <div className={`relative flex h-40 w-full flex-col justify-end bg-gradient-to-br ${service.heroClass} p-6`}>
                <div className="absolute right-4 top-4 flex items-center gap-1 rounded-md border border-white/10 bg-black/50 px-2 py-1 text-xs text-white backdrop-blur-md">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  {service.rating}
                </div>
                {service.icon}
                <h3 className="text-xl font-bold text-white">{service.title}</h3>
              </div>

              <div className="flex flex-1 flex-col p-5">
                <div className="mb-2 flex items-center gap-2 text-xs text-zinc-400">
                  {service.vendorOfficial ? (
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <div className="h-4 w-4 rounded-full bg-zinc-600" />
                  )}
                  <span>Por {service.vendor}</span>
                </div>
                <p className="mb-4 text-sm leading-6 text-zinc-300">
                  {service.description}
                </p>
                <div className="mt-auto flex items-end justify-between gap-4">
                  <div>
                    {service.oldPrice ? (
                      <span className="text-xs text-zinc-500 line-through">
                        {service.oldPrice}
                      </span>
                    ) : null}
                    <div className={["text-lg font-bold", service.accentPrice ? "text-[#22d3ee]" : "text-white"].join(" ")}>
                      {service.price}
                      {service.priceNote ? (
                        <span className="text-sm font-normal text-zinc-400">
                          {service.priceNote}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5b5cff]">
                    {service.buttonLabel}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

      </div>
    </div>
  );
}